import sync from 'src/providers/watermelondb/sync';
import {
  acquireSyncLock,
  releaseSyncLock,
  isSyncInProgress,
  waitForSyncToComplete,
} from 'src/common-utils/syncLock';

export const syncWithRetry = async (
  params: any,
  options = {
    maxRetries: 3,
    retryDelay: 2000,
    waitForExisting: true,
    maxWaitTime: 30000,
  },
) => {
  const {maxRetries, retryDelay, waitForExisting, maxWaitTime} = options;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (isSyncInProgress()) {
      console.log(`Sync in progress, attempt ${attempt + 1}/${maxRetries}`);

      if (waitForExisting && attempt === 0) {
        // On first attempt - wait for existing sync to complete
        try {
          console.log('Waiting for existing sync to complete...');
          await waitForSyncToComplete(maxWaitTime);
          console.log('Existing sync completed, proceeding...');
        } catch (error) {
          console.log('Timeout waiting for sync, will retry...');
        }
      } else {
        console.log(`Waiting ${retryDelay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }

      // Check again after waiting
      if (isSyncInProgress() && attempt < maxRetries - 1) {
        continue;
      }
    }

    // Try to acquire lock
    const acquired = await acquireSyncLock({
      type: params.onLogin
        ? 'login'
        : params.onDashboard
        ? 'dashboard'
        : 'organization',
      partition: params.partition,
      timestamp: Date.now(),
    });

    if (!acquired) {
      if (attempt < maxRetries - 1) {
        console.log(`Failed to acquire lock, retrying in ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      } else {
        throw new Error('Failed to acquire sync lock after multiple attempts');
      }
    }

    // Lock acquired, perform sync
    try {
      const result = await sync(params);
      return result;
    } catch (error) {
      console.error('Sync error:', error);
      throw error;
    } finally {
      releaseSyncLock();
    }
  }

  throw new Error(`Failed to complete sync after ${maxRetries} attempts`);
};

export default syncWithRetry;
